// Booking template has no cooldown — a patient booking a second test isn't
// "resubmitting the same lead", it's a genuinely new booking, and blocking
// it for 5 hours just prevented real bookings. Default/OTP templates keep
// the real cooldown, since those ARE the same-lead-resubmission case it's
// meant to guard against.
export const DEFAULT_COOLDOWN_HOURS = 5;
export const BOOKING_COOLDOWN_HOURS = 0;
export const COOLDOWN_KEY = 'curelo_form_submitted';

const getCooldownHoursForTemplate = (template) =>
  template === 'booking' ? BOOKING_COOLDOWN_HOURS : DEFAULT_COOLDOWN_HOURS;

const getCooldownEntry = () => {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return null;

    // Legacy: plain timestamp string
    if (/^\d+$/.test(raw)) {
      return { phone: null, submittedAt: parseInt(raw, 10) };
    }

    const parsed = JSON.parse(raw);
    if (parsed?.submittedAt == null) return null;
    return {
      phone: parsed.phone ? String(parsed.phone) : null,
      submittedAt: Number(parsed.submittedAt),
    };
  } catch {
    return null;
  }
};

export const isInCooldown = (phone, template) => {
  const cooldownMs = getCooldownHoursForTemplate(template) * 60 * 60 * 1000;
  if (cooldownMs <= 0) return false;

  try {
    const entry = getCooldownEntry();
    if (!entry?.submittedAt || Number.isNaN(entry.submittedAt)) {
      localStorage.removeItem(COOLDOWN_KEY);
      return false;
    }

    const elapsed = Date.now() - entry.submittedAt;
    if (elapsed >= cooldownMs) {
      localStorage.removeItem(COOLDOWN_KEY);
      return false;
    }

    if (phone && entry.phone && entry.phone !== String(phone)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

export const setCooldown = (phone, template) => {
  // Booking submissions don't start a cooldown either — otherwise a
  // booking-template submission would still block a LATER default/OTP
  // page visit for the same phone, which isn't what "no restriction for
  // booking" is supposed to mean.
  if (getCooldownHoursForTemplate(template) <= 0) return;

  try {
    localStorage.setItem(
      COOLDOWN_KEY,
      JSON.stringify({
        phone: phone ? String(phone) : null,
        submittedAt: Date.now(),
      }),
    );
    window.dispatchEvent(new Event('curelo-lead-submitted'));
  } catch {
    // localStorage unavailable (private mode, etc.)
  }
};

// Session-scoped (tab-local, cleared on tab close) — separate from the
// phone-keyed, cross-session COOLDOWN_KEY above. That one dedupes actual
// resubmission of the SAME phone within 5 hours and must stay phone-scoped.
// This one only answers "did THIS tab already complete a submission", so a
// second, different visitor on the same browser/device (or the same device
// reused hours apart by someone else) doesn't land on the success screen
// for a submission that isn't theirs.
const SESSION_SUCCESS_KEY = 'curelo_session_submitted';

export const markSessionSuccess = () => {
  try {
    sessionStorage.setItem(SESSION_SUCCESS_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode, etc.)
  }
};

export const hasSessionSuccess = () => {
  try {
    return sessionStorage.getItem(SESSION_SUCCESS_KEY) === '1';
  } catch {
    return false;
  }
};
